/**
 * One call, the right-hand half of the simplified register.
 *
 * FOUR THINGS, TOP TO BOTTOM, IN THE ORDER A PERSON AT A COUNTER NEEDS THEM: who it was and
 * a way to ring them, what they asked, what the assistant made of it, and whether anybody
 * still has to do something. The number is the biggest thing on the screen because it is
 * the thing most often acted on - a `tel:` link, so on a tablet with a SIM it dials.
 *
 * "WHAT TO DO NEXT" IS THE ONE ADDITION TO THE PROTOTYPE THIS CAME FROM. It is the reading's
 * `followUp`, the analyser's single instruction for whoever picks the call up, and it is
 * the most directly useful sentence the product writes about a call. A mechanic reading
 * "Call back before 3pm with a quote for the brake pads" does not need the rest.
 *
 * THE HEADER IS DRAWN FROM THE LIST ROW WHILE THE DETAIL LOADS, the same trick the call
 * dialog uses: stepping down the register changes the number and the question at once and
 * fills the summary a beat later.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { useApi, type CallDetail, type CallSummary } from "../api";
import { useCurrentAgent } from "../currentAgent";
import { duration, fullWhen, numberOf, phone } from "../format";
import { Button, cx } from "../ui";
import { HandledControl, callStatus } from "./Handled";
import { ArrowLeft, Phone, Search } from "./Icons";
import { SimpleTranscript } from "./SimpleTranscript";
import { StatusPill, titleOf } from "./SimpleRow";
import { Empty, Skeleton } from "./States";

/** A heading over one part of the pane. Words, not an uppercase eyebrow: this is read aloud. */
function Label({ children }: { children: string }) {
  return <h2 className="text-lg font-medium text-ink-soft">{children}</h2>;
}

const RULE = "border-t-2 border-line-soft pt-5";

export function SimpleCallPane({
  id,
  row,
  backHref,
  onChanged,
}: {
  id: string;
  /** The list's copy of this call, which draws the header before the detail arrives. */
  row: CallSummary | undefined;
  /** Back to the register, for a screen too narrow to show both halves at once. */
  backHref: string;
  /** The call as the Worker has it after somebody marked it, for the list to take. */
  onChanged: (call: CallDetail) => void;
}) {
  const { current } = useCurrentAgent();
  const tz = current?.timezone;
  const { data, error } = useApi<{ call: CallDetail }>(`/v1/calls/${id}`);
  // The call as the last write returned it. See the same state in `CallDialog`.
  const [saved, setSaved] = useState<CallDetail | null>(null);
  const [reading, setReading] = useState(false);

  const detail = saved && String(saved.id) === id ? saved : (data?.call ?? null);
  const call: CallSummary | undefined = detail ?? row;

  const back = (
    <Link
      to={backHref}
      className="hidden h-14 items-center gap-2 self-start text-xl font-medium text-accent max-[900px]:flex"
    >
      <ArrowLeft size={22} />
      Back to the list
    </Link>
  );

  if (!call) {
    return (
      <div className="grid content-start gap-5 p-6">
        {back}
        {error ? (
          <Empty
            compact
            icon={error.status === 404 ? Search : undefined}
            tone={error.status === 404 ? undefined : "alert"}
            title={error.status === 404 ? "No such call" : "Couldn't load this call"}
            body={
              error.status === 404
                ? "It may belong to another business, or it may have aged out of the record."
                : error.message
            }
          />
        ) : (
          <>
            <Skeleton width={320} height={40} />
            <Skeleton width={240} height={22} />
            <Skeleton height={120} />
          </>
        )}
      </div>
    );
  }

  const number = numberOf(detail ?? call);
  // The row's own line, so a reader matching the pane to the row they tapped reads the same
  // words at the top of both. The summary below it is a different field, never a repeat.
  const question = call.analysis.ask;
  const summary = call.analysis.summary;
  const next = detail?.analysis.followUp ?? null;

  return (
    <div className="grid content-start gap-5 p-6 max-[900px]:p-4">
      {back}

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="grid min-w-0 gap-2">
          {number ? (
            <a
              href={`tel:${number.replace(/\s/g, "")}`}
              className="nums flex min-h-14 items-center gap-3 text-5xl font-bold tracking-[-0.02em] text-ink hover:text-accent"
            >
              <Phone size={34} className="flex-none" />
              <span className="truncate">{phone(number)}</span>
            </a>
          ) : (
            <p className="text-5xl font-bold tracking-[-0.02em] text-ink">{titleOf(call)}</p>
          )}
          <p className="text-xl text-ink-soft">
            {/* THE ROW'S TITLE, WHEN THE PANE'S IS DIFFERENT. Only the detail carries the
                number the agent took down, so a call the list titles "Caller B97E" can be
                titled by that number here - and a reader matching the pane to the row they
                tapped needs the word the row said. */}
            {row && titleOf(row) !== phone(number) && <>{titleOf(row)} · </>}
            Called {fullWhen(call.startedAt, tz)} · {duration(call.durationMs)}
          </p>
        </div>
        <StatusPill call={call} className="mt-2" />
      </header>

      <section className={cx(RULE, "grid gap-2")}>
        <Label>What they wanted</Label>
        <p className="text-4xl leading-tight font-bold text-ink">
          {question ?? "The assistant hasn't written this call up yet."}
        </p>
      </section>

      {(summary || next || detail) && (
        <section className={cx(RULE, "grid gap-4")}>
          {summary && (
            <div className="grid gap-2">
              <Label>Call summary</Label>
              <p className="text-xl leading-normal text-ink">{summary}</p>
            </div>
          )}
          {next && (
            <div className="grid gap-2">
              <Label>What to do next</Label>
              <p className="text-xl leading-normal text-ink">{next}</p>
            </div>
          )}
          <Button
            size="large"
            className="justify-self-start"
            disabled={!detail}
            onClick={() => setReading(true)}
          >
            View transcript
          </Button>
        </section>
      )}

      <section className={RULE}>
        {/* A call the assistant dealt with gets a sentence rather than an empty rule: the
            large control draws nothing for it, on purpose, and a blank band at the foot of
            the pane reads as something that failed to load. */}
        {callStatus(call).kind === "handled" ? (
          <p className="text-xl text-ink-soft">Your assistant dealt with this call. Nothing to do.</p>
        ) : (
          <HandledControl
            call={call}
            size="large"
            onChanged={(changed) => {
              setSaved(changed);
              onChanged(changed);
            }}
          />
        )}
      </section>

      {reading && detail && (
        <SimpleTranscript call={detail} tz={tz} onClose={() => setReading(false)} />
      )}
    </div>
  );
}
